<template>
<div id="sheet-main">
  <div v-if="surveillance != null">
    <div v-if="sheetsAvailable">
      <action-bar id="action-bar"
                  ref="actionBar"
                  v-sticky :actions="shortcuts"></action-bar>
      <div v-show="!available[type]" class="container-cap">
        <alert type="warning">
          <p>
            <strong>En attente de récupération des fiches...</strong>
          </p>
        </alert>
      </div>
      <div v-if="available[type]">
        <div v-show="!state.list" class="container-cap">
          <router-view :config="config"
                       :state="state"
                       :surveillance="surveillance"
                       :sheets="sheets[type]"
                       ref="subpage"></router-view>
        </div>
        <div v-show="state.list" class="container-cap">
          <div class="row">
            <div class="col-xs-12 col-sm-6 col-md-5 col-lg-4">
              <div class="panel panel-default">
                <div class="panel-heading" >
                  <div class="panel-title">
                    Fiches
                    <span class="label" :class="`label-${type}`">
                      {{ modules[type].label }}
                    </span>
                  </div>
                </div>
                <div class="panel-body">
                  <p>
                    {{ lists.valid.length }} fiche.s contrôlée.s.<br/>
                    <span v-if="canExportSheets && lists.valid.length > 0">
                      <export-sheets :surveillance="surveillance" :type="type">
                      </export-sheets>
                    </span>
                  </p>

                  <p>
                    {{ lists.open.length }} fiche.s en cours de saisie.<br/>
                    <span v-if="canExportSheets && lists.open.length > 0">
                      <export-sheets :surveillance="surveillance" :type="type"
                      export-open>
                      </export-sheets>
                    </span>

                  </p>
                </div>
                <div class="panel-footer" v-if="canValidateSheets">
                  <validate-sheets :surveillance="surveillance"
                                   :type="type"
                                   :sheets="sheets[type]">
                  </validate-sheets>
                </div>
              </div>
            </div>
          </div>
          <h3 class="list-title">
            {{ sheetType }} ({{ list.length }})
          </h3>
          <list :columns="table.columns"
                :actions="tableActions"
                :keyfield="table.keyfield"
                :list="list"
                :selected="$route.params.sid"
                :type="listType"
                initialSort="sheetNumber"
                @edit="edit($event)"
                @delete="askForDelete($event)">
          </list>
        </div>
      </div>
      <span v-for="(s, i) in shortcuts"
            v-if="(s.nav == null || s.nav()) && (s.disabled == null || !s.disabled())"
            :key="`sheet-main-shortcut-${i}`"
            v-shortkey.once.prevent="s.keys"
            @shortkey="s.action(s.param)">
      </span>
      <modal v-model="removeModal"
             @hide="removeModalEnded"
             title="Confirmation"
             ok-text="Oui" cancel-text="Non"
             ok-type="danger" cancel-type="primary">
        <p v-if="removeModal">
          Êtes vous certain.e de vouloir supprimer cette fiche ?
        </p>
      </modal>
    </div>
    <div v-else class="container-cap">
      <div class="row">
        <div class="col-lg-9">
          <h2>
            Module de saisie
            <span class="label" ref="title" :class="`label-${type}`">
              {{ moduleName }}
            </span>
          </h2>
          <hr />
          <alert type="warning" class="text-justify">
            <p>
              <strong>
                Le module de saisie est en train d'être adapté aux protocoles
                {{year}} et sera ouvert très prochainement.
              </strong>
            </p>
            <p>
              Cette page vous permettra d'ajouter, modifier et contrôler vos
              fiches pour la surveillance.
            </p>
          </alert>
        </div>
      </div>
    </div>
  </div>
</div>
</template>
<script>
import actionBar from '@/sui/components/actionBar'
import validateSheets from '@/sui/components/sheet/validate/sheets'
import exportSheets from '@/sui/components/sheet/export'
import list from '@/sui/components/sheet/list'
import units from '@/sui/data/units'
import modules from '@/sui/data/modules'
import assert from '@/sui/tools/assert'

import { mapState } from 'vuex'

const errors = {
  std: `Si le problème persiste, contactez-nous via le formulaire de contact.`,
  net: `Une erreur s'est produite lors de la connexion au serveur, merci de
vérifier votre connexion internet avant de réessayer. Si le problème persiste,
contactez-nous via le formulaire de contact.`
}

export default {
  name: 'sheet-main',

  props: {
    config: {
      type: Object,
      required: true
    },

    surveillance: {
      type: Object
    },

    routeBuilder: {
      type: Function,
      default: (y, s, m) => `/establishments/${y}/${s}/module/${m}/sheets`
    },

    readOnly: {
      type: Boolean,
      default: false
    }
  },

  data: function () {
    const vm = this
    const state = {
      list: this.$route.query.start === 'list',
      refreshing: false
    }

    return {
      removeModal: false,
      actionSheet: null,
      cooldown: false,
      filter: 'open',
      state: state,
      shortcuts: [
        {
          keys: { h: ['f2'] },
          action: () => {
            this.state.list = !this.state.list
          }
        },
        {
          keys: null,
          keyText: `<span class="key">F2</span>`,
          id: 'edit',
          icon: 'file',
          nav: () => true,
          tooltip: () => `Vue <strong>Fiche</strong>`,
          active: () => !state.list,
          action: () => { this.state.list = false },
          text: 'Vue Fiche'
        },
        {
          keys: null,
          keyText: `<span class="key">F2</span>`,
          id: 'list',
          icon: 'list',
          nav: () => true,
          tooltip: () => `Vue <strong>Liste</strong>`,
          active: () => state.list,
          action: () => { this.state.list = true },
          text: 'Vue Liste'
        },
        {},
        {
          keys: null,
          action: this.refresh,
          keyText: ``,
          icon: 'refresh',
          nav: () => state.list,
          disabled: () => state.refreshing,
          tooltip: () => '<strong>Rafraîchit</strong> la liste des fiches',
          text: 'Rafraîchir'
        },
        {
          keys: { h: ['f4'] },
          action: this.newSheet,
          keyText: `<span class="key">F4</span>`,
          icon: 'file',
          nav: function () {
            return state.list || vm.$route.params.sid != null
          },
          tooltip: () => 'Ouvre une <strong>nouvelle fiche</strong>',
          text: 'Nouvelle Fiche'
        },
        {
          action: this.save,
          keys: { h: ['f3'] },
          keyText: `<span class="key">F3</span>`,
          icon: 'floppy-save',
          nav: () => {
            return !this.readOnly && !state.list
          },
          tooltip: () => '<strong>Enregistre</strong> la fiche en cours',
          text: 'Enregistrer'
        },
        {
          keys: null,
          action: this.refreshSheet,
          keyText: ``,
          icon: 'refresh',
          nav: () => this.readOnly && !state.list,
          tooltip: () => '<strong>Rafraîchit</strong> la fiche en cours',
          text: 'Rafraîchir'
        },
        {
          keys: null,
          action: this.askForRemoval,
          keyText: ``,
          icon: 'trash',
          nav: function () {
            const sheet = vm.$store.state.sheet.edit
            return !this.readOnly && !state.list &&
                   vm.canEditSheets && vm.$route.params.sid != null &&
                   sheet != null
          },
          tooltip: () => '<strong>Supprime</strong> la fiche en cours',
          text: 'Supprimer'
        },
        {
          keys: null,
          action: () => { this.filter = 'open' },
          keyText: ``,
          icon: 'pencil',
          nav: () => state.list,
          active: () => { return !this.validFilter },
          tooltip: () => `Affiche uniquement les fiches
<strong class="text-primary">en cours de saisie</strong>`,
          text: 'En cours'

        },
        {
          keys: null,
          action: () => { this.filter = 'valid' },
          keyText: ``,
          icon: 'ok',
          nav: () => state.list,
          active: () => { return this.validFilter },
          tooltip: () => `Affiche uniquement les fiches
<strong class="text-success">contrôlées</strong>`,
          success: () => true,
          text: 'Contrôlées'
        },
        {},
        {
          keys: { h: ['f8'] },
          keyText: `<span class="key">F8</span>`,
          icon: 'chevron-left',
          tooltip: () => `Fiche <strong>${this.currentSheetStatus}</strong> précédente`,
          text: '',
          nav: () => {
            const sid = vm.$route.params.sid
            return !state.list && sid != null
          },
          disabled: () => this.cooldown || this.firstSheet,
          action: () => { this.move(-1) },
          success: () => this.validSheet
        },
        {
          keys: { h: ['f9'] },
          keyText: `<span class="key">F9</span>`,
          icon: 'chevron-right',
          tooltip: () => `Fiche <strong>${this.currentSheetStatus}</strong> suivante`,
          nav: () => {
            const sid = vm.$route.params.sid
            return !state.list && sid != null
          },
          disabled: () => this.cooldown || this.lastSheet,
          action: () => { this.move(1) },
          success: () => this.validSheet
        }
      ]
    }
  },

  computed: {
    ...mapState({
      sheets: state => state.sheet.all,
      available: state => state.sheet.available,
      userId: state => state.user.info.id,
      currentSheet: state => state.sheet.edit
    }),

    modules: function () {
      return modules[this.year]
    },

    sheetsAvailable: function () {
      return this.modules[this.type].sheets.edit != null
    },

    table: function () {
      const columns = [
        {
          name: 'Numéro',
          field: 'sheetNumber'
        }
      ]
      if (this.type === 'observa4' || this.type === 'expADI') {
        columns.push(
          { name: 'Type', field: 'type' }
        )
      }

      columns.push(
        {
          name: 'Code service',
          field: 'unit'
        }
      )

      if (this.type === 'spy4') {
        columns.push(
          {
            name: 'Détail des CVPs',
            field: 'type'
          }
        )
      }

      if (this.type === 'badiCause') {
        columns.push(
          {
            name: `Date de l'analyse`,
            field: 'badiCauseAnalysisDate'
          }
        )
      }

      if (this.type !== 'observa4' &&
          this.type !== 'spy4') {
        columns.push(
          {
            name: 'Sexe',
            field: 'sex'
          },
          {
            name: 'Date de naissance',
            field: 'birthdate'
          }
        )
      }

      columns.push(
        {
          name: 'Fiche créée le',
          field: 'creation'
        },
        {
          name: 'Dernière modif.',
          field: 'lastEdit'
        },
        {
          name: 'Dernière modif. par',
          field: 'lastEditor'
        }
      )
      return {
        columns: columns,
        keyfield: 'id'
      }
    },

    currentSheetStatus: function () {
      if (this.currentSheet == null) {
        return null
      }

      const st = this.currentSheet.st
      if (st === 'valid') {
        return `<span class="text-success">contrôlée</span>`
      }
      return `<span class="text-primary">en cours de saisie</span>`
    },

    validSheet: function () {
      return this.currentSheet != null && this.currentSheet.st === 'valid'
    },

    firstSheet: function () {
      return this.sheetIndex === 0
    },

    lastSheet: function () {
      const sheet = this.currentSheet
      if (sheet == null) {
        return false
      }

      return this.sheetIndex === this.lists[sheet.st].length - 1
    },

    sheetIndex: function () {
      const sheet = this.currentSheet
      if (sheet == null) {
        return -1
      }

      return this.lists[sheet.st].findIndex(s => s.id === sheet.id)
    },

    year: function () {
      return this.$route.params.year
    },

    type: function () {
      return this.$route.params.st
    },

    moduleName: function () {
      return this.modules[this.type].label
    },

    validFilter: function () {
      return this.filter === 'valid'
    },

    listType: function () {
      return this.validFilter ? 'success' : 'info'
    },

    sheetType: function () {
      return this.validFilter ? 'Fiche contrôlées' : 'Fiches en cours de saisie'
    },

    canValidateSheets: function () {
      if (this.surveillance == null) {
        return false
      }

      return this.surveillance.canValidateSheets(this.userId, this.type)
    },

    canEditSheets: function () {
      if (this.surveillance == null) {
        return false
      }

      return this.surveillance.canEditSheets(this.userId, this.type)
    },

    canExportSheets: function () {
      const mod = this.modules[this.type]
      return this.canValidateSheets && mod.sheets != null &&
             mod.sheets.flatten != null
    },

    tableActions: function () {
      const actions = [
        {
          event: 'edit',
          name: 'Modifier'
        }
      ]

      if (this.canEditSheets) {
        actions.push(
          {
            event: 'delete',
            name: 'Supprimer'
          }
        )
      }
      return actions
    },

    routePrefix: function () {
      if (this.surveillance == null) {
        return ''
      }

      const { year, id } = this.surveillance
      const t = this.type
      return this.routeBuilder(year, id, t)
    },

    formattedlist () {
      let list = this.sheets[this.type]

      const userFormatter = (user) => {
        return user.name.concat(' ', user.firstName)
      }
      const sexFormatter = (sex) => {
        switch (sex) {
          case 1: return '1. Masculin'
          case 2 : return '2. Féminin'
          case 3 : return '3. Autre'
          case 9 : return '9. Non connu'
          default : return ''
        }
      }

      const utFormatter = (unitType, uf) => {
        if (unitType == null) {
          return ''
        }

        const unit = units[this.year].list.find(e => e.index === unitType)
        assert(unit != null, `Unknown unitType: ${unitType}`)
        if (unit == null) {
          return ''
        }

        if (uf !== '' && uf != null) {
          return `${uf} - ${unit.code}`
        }

        return unit.code
      }

      const typeFormatter = (type) => {
        if (this.type === 'observa4') {
          return this.modules.observa4.format.obsManagment(type)
        }

        if (this.type === 'expADI') {
          switch (type) {
            case 1: return 'Bactériémie'
            case 2: return 'Pneumopathie'
            case 3: return 'Sepsis'
          }
          return ''
        }

        if (this.type === 'spy4') {
          switch (type) {
            case 1: return 'Oui'
            case 2: return 'Non'
          }
        }

        return null
      }

      const reducer = (result, s) => {
        let v = {
          'sheetNumber': s.sheetNumber,
          'unit': utFormatter(s.unitType, s.uf),
          'sex': sexFormatter(s.sex),
          'birthdate': s.birthdate,
          'creation': s.creation,
          'badiCauseAnalysisDate': s.badiCauseAnalysisDate,
          'type': typeFormatter(s.type),
          'lastEdit': s.lastEdit,
          'lastEditor': userFormatter(s.lastEditor),
          'id': s.id,
          'status': s.status
        }
        result.push(v)
        return result
      }

      const result = list.reduce(reducer, [])
      return result.sort((first, second) => first.sheetNumber - second.sheetNumber)
    },

    lists () {
      const result = {}
      const filters = ['open', 'valid']
      filters.forEach(
        f => {
          const list = this.formattedlist
          const reducer = (result, u) => {
            if (u.status === f) {
              result.push(u)
            }
            return result
          }
          result[f] = list.reduce(reducer, [])
        }
      )
      return result
    },

    list () {
      return this.lists[this.filter]
    }
  },

  watch: {
    routePrefix: function () {
      this.state.list = this.$route.query.start === 'list'
    }
  },

  methods: {
    move: function (v) {
      const sid = this.$route.params.sid
      if (this.cooldown || sid == null || this.sheetIndex === -1) {
        return
      }

      const newIndex = this.sheetIndex + v
      if (newIndex < 0 || newIndex >= list.length) {
        return
      }

      this.cooldown = true
      this.edit(this.lists[this.currentSheet.st][newIndex])
      setTimeout(this.resetCooldown, 300)
    },

    resetCooldown: function () {
      this.cooldown = false
    },

    edit: function (s) {
      this.state.list = false
      this.$router.push(`${this.routePrefix}/${s.id}/edit`)
    },

    save: function () {
      this.$refs.subpage.save()
    },

    refreshSheet: function () {
      this.$refs.subpage.refresh()
    },

    refresh: function () {
      this.state.refreshing = true
      const vm = this
      const request = {
        t: this.type,
        s: this.surveillance.id,
        y: this.surveillance.year
      }

      this.$store.dispatch(`sheet/load`, request).then(
        (response) => {
          vm.state.refreshing = false
          if (response.data.err != null) {
            vm.$notify({
              type: 'danger',
              title: `Impossible de rafraîchir la liste !`,
              duration: 0,
              content: `${errors.std}`
            })
          }
        }
      ).catch(function (e) {
        vm.state.refreshing = false
        vm.$notify({
          type: 'danger',
          title: `Impossible de rafraîchir la liste !`,
          duration: 0,
          content: `${errors.net}`
        })
      })
    },

    newSheet: function () {
      this.state.list = false
      const sid = this.$route.params.sid
      if (sid) {
        this.$router.push(`${this.routePrefix}/add`)
      }
    },

    askForValidation: function () {
    },

    askForRemoval: function () {
      this.removeModal = true
    },

    removeModalEnded: function (msg) {
      if (msg === 'ok') {
        if (this.actionSheet != null) {
          this.remove(this.actionSheet)
        } else {
          const sid = this.$route.params.sid
          this.remove(sid)
        }
        this.actionSheet = null
      }
      this.removeModal = false
    },

    remove (sid) {
      this.state.list = true
      let request = {
        t: this.type,
        s: this.surveillance.id,
        y: this.surveillance.year,
        id: sid
      }
      let instance = this
      this.$store.dispatch(`sheet/remove`, request).then(
        (response) => {
          if (response.data.err == null) {
            instance.$notify({
              type: 'success',
              title: 'Succès !',
              content: `La fiche a bien été supprimée.`
            })
            this.$router.push(`${this.routePrefix}/add?start=list`)
          } else {
            const errCode = response.data.err.match((/\[(.*?)\]/))[1]
            var e = ''
            switch (errCode) {
              case 'ClosedSurveillance':
                e = `Surveillance close : il n'est plus possible de 
                supprimer cette fiche.`
                break
              default:e = `${errors.std}`
            }
            instance.$notify({
              type: 'danger',
              title: `Impossible de supprimer cette fiche !`,
              duration: 0,
              content: e
            })
          }
        }).catch(function (e) {
        instance.$notify({
          type: 'danger',
          title: `Impossible de supprimer cette fiche !`,
          duration: 0,
          content: `${errors.net}`
        })
      })
    },

    askForDelete: function (s) {
      this.actionSheet = s.id
      this.removeModal = true
    }
  },

  beforeRouteUpdate: function (to, from, next) {
    if (this.$refs.subpage != null) {
      this.$refs.subpage.checkRoute(to, from, next)
    } else {
      next()
    }
  },

  beforeRouteLeave: function (to, from, next) {
    if (this.$refs.subpage != null) {
      this.$refs.subpage.checkRoute(to, from, next)
    } else {
      next()
    }
  },

  components: {
    'action-bar': actionBar,
    'list': list,
    'validate-sheets': validateSheets,
    'export-sheets': exportSheets
  }
}
</script>
<style scoped lang="less">
@import '~variables';

#sheet-main {
  padding-bottom: 50rem;
  .list-title {
    .btn {
    }
  }
  #action-bar {
    @media (min-width: 0) and (max-width: @screen-sm-max) {
      margin-left: 23rem;
    }
  }
}

.panel {
  margin-top: 2rem;
  margin-bottom: 0rem;
  color: @dark-base;

  .panel-title {
    font-size: 140%;
    color: @dark-base;
    .label {
      font-size: 90%;
    }
  }
}
</style>
